import { useState } from 'react';

import { Box, useMediaQuery } from '@mui/material';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { LupaDeImagen } from '@/components/LupaDeImagen';

import 'swiper/css';
import 'swiper/css/pagination';

const MINIATURA = 60;
const ANCHO_DE_MINIATURAS = 70;

/**
 * Las fotos del producto: miniaturas al costado y la grande con lupa.
 *
 * En el teléfono no hay lupa ni miniaturas —no hay mouse y no hay lugar—: se pasa el dedo. Es
 * lo mismo que hacía antes, con el carrusel que ya usa el resto del sitio en vez de una segunda
 * biblioteca sólo para esta pantalla.
 */
export function GaleriaDeProducto({ fotos }: { fotos: string[] }) {
  const [actual, setActual] = useState(0);
  const enElTelefono = useMediaQuery('(max-width:1000px)');

  if (fotos.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 400,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#000',
          color: '#fff',
          textAlign: 'center',
          p: 2,
        }}
      >
        No hay imágenes disponibles para este producto
      </Box>
    );
  }

  if (enElTelefono) {
    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <Swiper modules={[Pagination]} pagination={{ clickable: true }} loop={fotos.length > 1}>
          {fotos.map((foto, indice) => (
            <SwiperSlide key={foto}>
              <Box
                component="img"
                src={foto}
                alt={`Imagen ${indice + 1}`}
                // Mismo fondo blanco que en la vista de escritorio, por lo mismo: las fotos
                // vienen con fondo blanco y sin esto el teléfono mostraba el recorte del
                // archivo sobre el negro de la página.
                sx={{
                  width: '100%',
                  height: 500,
                  objectFit: 'contain',
                  borderRadius: 2,
                  bgcolor: '#fff',
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    );
  }

  const foto = fotos[actual] ?? fotos[0]!;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: '50vh',
          overflowY: 'auto',
          flexShrink: 0,
          width: ANCHO_DE_MINIATURAS,
        }}
      >
        {fotos.map((miniatura, indice) => (
          <Box
            key={miniatura}
            role="button"
            tabIndex={0}
            aria-label={`Ver imagen ${indice + 1}`}
            onClick={() => setActual(indice)}
            onKeyDown={evento => {
              if (evento.key === 'Enter' || evento.key === ' ') {
                evento.preventDefault();
                setActual(indice);
              }
            }}
            sx={{
              width: MINIATURA,
              height: MINIATURA,
              flexShrink: 0,
              display: 'flex',
              overflow: 'hidden',
              borderRadius: 1.5,
              // El borde de la elegida era #3483fa, un azul que no aparece en ninguna otra
              // parte del sitio.
              border: '2px solid',
              borderColor: indice === actual ? 'primary.main' : 'rgba(255,255,255,.14)',
              cursor: 'pointer',
              // El blanco va en el marco y la atenuación en la foto, separados. Cuando los dos
              // estaban en el mismo <img>, la opacidad atenuaba también el fondo: la miniatura
              // no elegida se veía gris sobre la página oscura en vez de blanca.
              backgroundColor: '#fff',
              transition: 'border-color .25s ease, transform .25s ease',
              '&:hover': { transform: 'translateY(-2px)' },
              '&:hover .foto-miniatura': { opacity: 1 },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'border-color .25s ease',
                '&:hover': { transform: 'none' },
              },
            }}
          >
            <Box
              component="img"
              className="foto-miniatura"
              src={miniatura}
              // El marco ya se anuncia con su aria-label; repetir acá haría que el lector de
              // pantalla diga dos veces lo mismo.
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                // `contain` y no `cover`: estas fotos traen el producto centrado con aire
                // alrededor, y recortarlas al cuadrado le comía las puntas a todo lo alargado
                // -una antena, una cadena- justo en la vista que sirve para elegir cuál mirar.
                objectFit: 'contain',
                opacity: indice === actual ? 1 : 0.55,
                transition: 'opacity .25s ease',
              }}
            />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: 'clamp(300px, 45vw, 500px)',
          height: 'clamp(300px, 50vh, 500px)',
          // Blanco y no negro. Las fotos del catálogo vienen con fondo blanco, así que sobre
          // negro quedaba un rectángulo flotando entre dos bandas oscuras enormes: se veía el
          // recorte del archivo antes que el producto. Sobre blanco la foto ocupa el cuadro
          // entero y el borde apenas la enmarca.
          backgroundColor: '#fff',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,.10)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <LupaDeImagen
          src={foto}
          alt={`Imagen ${actual + 1}`}
          fotos={fotos}
          actual={actual}
          onIr={setActual}
        />
      </Box>
    </Box>
  );
}
